import { authenticatedFetch } from "./apiClient";
import type { RecipeOwner } from "./recipeService";

// Hermes worker is mounted on the api LB under /agent/* (prefix rewritten
// away), so it shares VITE_API_URL with core.
const AGENT_BASE_URL = `${import.meta.env.VITE_API_URL}/agent`;

export interface HermesThread {
  thread_id: string;
  mode: string;
  title: string | null;
  domain_id: string;
  model_default: string;
  trigger_meta: Record<string, unknown>;
  status: string;
  created_at: string;
  updated_at: string;
  last_run_id: string | null;
}

export interface StreamTurnCallbacks {
  /** Incremental agent working-notes text (advisory; shown in a collapsed panel). */
  onTrace: (chunk: string) => void;
  /** Incremental narrator-polished answer text (the canonical answer). */
  onFinal: (chunk: string) => void;
}

interface SseFrame {
  type?: string;
  content?: string;
  tool_use?: { id?: string; name?: string };
}

async function throwIfNotOk(response: Response, action: string): Promise<void> {
  if (response.ok) return;
  if (response.status === 401 || response.status === 403) {
    throw new Error("Your session has expired — please sign in again.");
  }
  let detail = response.statusText;
  try {
    const body: unknown = await response.json();
    if (
      typeof body === "object" &&
      body !== null &&
      typeof (body as { detail?: unknown }).detail === "string"
    ) {
      detail = (body as { detail: string }).detail;
    }
  } catch {
    // Non-JSON error body; statusText is good enough.
  }
  throw new Error(`${action} failed (${response.status}): ${detail}`);
}

/**
 * Read one turn's SSE stream to completion, dispatching frames to `callbacks`.
 *
 * Extracted from `streamTurn` when `invokeRecipe` became a second caller:
 * two copies of the framing, `[DONE]` handling and malformed-frame tolerance
 * is two frame parsers to fix the next time the event shape moves.
 */
async function consumeTurnStream(
  response: Response,
  callbacks: StreamTurnCallbacks,
): Promise<void> {
  if (!response.body) {
    throw new Error("The answer stream was empty.");
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let is_done = false;

  while (!is_done) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE events are separated by a blank line.
    let separatorIndex = buffer.indexOf("\n\n");
    while (separatorIndex !== -1) {
      const rawEvent = buffer.slice(0, separatorIndex);
      buffer = buffer.slice(separatorIndex + 2);
      separatorIndex = buffer.indexOf("\n\n");

      const data = rawEvent
        .split("\n")
        .filter((line) => line.startsWith("data:"))
        .map((line) => line.slice(5).trimStart())
        .join("\n");
      if (!data) continue;
      if (data === "[DONE]") {
        is_done = true;
        break;
      }

      let frame: SseFrame;
      try {
        frame = JSON.parse(data);
      } catch {
        continue; // tolerate malformed frames
      }
      if (frame.type === "trace") {
        if (typeof frame.content === "string") {
          callbacks.onTrace(frame.content);
        } else if (frame.tool_use?.name) {
          callbacks.onTrace(`\n→ ${frame.tool_use.name}\n`);
        }
      } else if (frame.type === "final" && typeof frame.content === "string") {
        callbacks.onFinal(frame.content);
      }
    }
  }

  if (!is_done) {
    throw new Error("Connection lost before the answer completed");
  }
}

export class HermesService {
  static async createThread(
    domainId: string,
    title?: string,
  ): Promise<HermesThread> {
    const response = await authenticatedFetch(
      `${AGENT_BASE_URL}/v1/domains/${domainId}/threads`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: "chat", title: title ?? null }),
      },
    );
    await throwIfNotOk(response, "Thread creation");
    return response.json();
  }

  static async archiveThread(
    domainId: string,
    threadId: string,
  ): Promise<HermesThread> {
    const response = await authenticatedFetch(
      `${AGENT_BASE_URL}/v1/domains/${domainId}/threads/${threadId}/archive`,
      { method: "POST" },
    );
    await throwIfNotOk(response, "Archiving conversation");
    return response.json();
  }

  /**
   * Send a user turn and stream the assistant's answer.
   *
   * SSE protocol (see app-hermes-worker app/proxy/sse.py):
   *   data: {"type":"trace","content":"..."}                       — working notes
   *   data: {"type":"trace","tool_use":{"id":"...","name":"..."}}  — tool started
   *   data: {"type":"final","content":"..."}                       — polished answer
   *   data: [DONE]                                                 — always last
   *
   * Resolves on [DONE]; rejects on HTTP error or a stream that dies
   * without [DONE]. content chunks are incremental — append them.
   */
  static async streamTurn(
    domainId: string,
    threadId: string,
    text: string,
    callbacks: StreamTurnCallbacks,
    signal?: AbortSignal,
    attachmentDocIds: string[] = [],
  ): Promise<void> {
    const response = await authenticatedFetch(
      `${AGENT_BASE_URL}/v1/domains/${domainId}/threads/${threadId}/turns`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_message: {
            // A reference, never bytes or a URL: the worker resolves it
            // server-side against the org the caller is verified into, so a
            // doc_id cannot reach another organization's file.
            content_blocks: [
              { type: "text", text },
              ...attachmentDocIds.map((doc_id) => ({
                type: "library_ref",
                doc_id,
              })),
            ],
          },
          idempotency_key: crypto.randomUUID(),
        }),
        signal,
      },
    );
    await throwIfNotOk(response, "Sending message");
    await consumeTurnStream(response, callbacks);
  }

  /**
   * Run a recipe: same event stream as a turn, on a thread the server creates.
   *
   * Returns the thread id from the `X-Thread-Id` response header. The thread is
   * created server-side before the stream opens and never appears in the body,
   * so this header is the only honest source — "the newest thread in the
   * subscription" is a race dressed up as a feature.
   *
   * Returns `null` rather than throwing when the header is absent: an older
   * hermes revision is a missing link, not a failed run, and the answer on
   * screen is still real.
   *
   * No `isViewerAllowed`: invoking writes a conversation into the domain's org,
   * which is org data. A viewer cannot chat and cannot invoke — one rule.
   */
  static async invokeRecipe(
    domainId: string,
    recipe: { owner: RecipeOwner; kind: string },
    params: Record<string, string>,
    callbacks: StreamTurnCallbacks,
    signal?: AbortSignal,
  ): Promise<{ threadId: string | null }> {
    const response = await authenticatedFetch(
      `${AGENT_BASE_URL}/v1/domains/${domainId}/threads/by-recipe`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          recipe,
          params,
          idempotency_key: crypto.randomUUID(),
        }),
        signal,
      },
    );
    await throwIfNotOk(response, "Running the recipe");
    const threadId = response.headers.get("X-Thread-Id");
    await consumeTurnStream(response, callbacks);
    return { threadId };
  }
}
